import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPhone, smsSegments } from '@fsm/shared';
import { api } from '../lib/api';
import { useSoftphone } from '../lib/softphone';
import { Button, Field, Modal, Textarea, Toast, cx } from './ui';

/**
 * Calling and texting a customer from wherever their number appears.
 *
 * Click-to-call places the call from the browser when the softphone is
 * registered (lib/softphone.tsx). Otherwise it rings the dispatcher's own
 * extension first and bridges them when they pick up, so nobody types a
 * number. If the phone system cannot
 * place calls — or none is configured — the button quietly degrades to a plain
 * `tel:` link rather than disappearing, because a dispatcher still needs to be
 * able to phone somebody.
 */

export function useTelephony() {
  return useQuery({
    queryKey: ['telephony', 'capabilities'],
    queryFn: () => api.telephony.capabilities(),
    staleTime: 10 * 60_000,
    // A missing phone system is a normal state, not an error worth retrying.
    retry: false,
  });
}

export interface CallActionsProps {
  phone: string | null | undefined;
  customerId?: string | null;
  jobId?: string | null;
  customerName?: string;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * The call mutation, the text modal and the toast, shared by the two shapes
 * this comes in — a pair of buttons, and a phone number that dials itself.
 */
function useCallAndText(phone: string | null | undefined, customerId?: string | null, jobId?: string | null) {
  const { data: telephony } = useTelephony();
  const [toast, setToast] = useState<{ message: string; tone: 'info' | 'error' | 'success' } | null>(
    null,
  );
  const [texting, setTexting] = useState(false);

  const softphone = useSoftphone();

  const bridge = useMutation({
    mutationFn: () => api.calls.clickToCall({ toNumber: phone!, customerId, jobId }),
    onSuccess: (result) => {
      setToast({
        message: result.ringing
          ? `Ringing ${formatPhone(result.ringing)} — pick up and we'll connect you`
          : 'Placing the call…',
        tone: 'info',
      });
    },
    onError: (error) =>
      setToast({
        message: (error as { message?: string }).message ?? 'Could not place the call',
        tone: 'error',
      }),
  });

  /*
   * From the browser when it is registered, through the bridge when it is
   * not. The browser is better — no extension to pick up, and the call bar
   * shows it — but a company with no Twilio number, or a tab still
   * connecting, should still be able to phone a customer.
   */
  const inBrowser = softphone.status === 'ready';
  const call = inBrowser
    ? {
        mutate: () => void softphone.dial(phone!),
        // One call at a time; a second click mid-call would be refused anyway.
        isPending: softphone.active !== null,
      }
    : { mutate: () => bridge.mutate(), isPending: bridge.isPending };

  return {
    call,
    toast,
    setToast,
    texting,
    setTexting,
    canDial: inBrowser || (telephony?.capabilities.voice ?? false),
    canText: telephony?.capabilities.sms ?? false,
  };
}

/**
 * A phone number that is the call button.
 *
 * A number sitting next to a button marked "Call" is two things that do one
 * thing, and the one that looks like a phone number is the one people press.
 * So the number dials, and only texting — which opens a composer and is not
 * what anyone expects from clicking a number — keeps a button of its own.
 */
export function PhoneLine({
  phone,
  customerId,
  jobId,
  customerName,
  className,
  textOnly,
}: Omit<CallActionsProps, 'size'> & {
  /**
   * Just the Text button, for a page heading where the number itself is
   * already printed on the card below.
   */
  textOnly?: boolean;
}) {
  const { call, toast, setToast, texting, setTexting, canDial, canText } = useCallAndText(
    phone,
    customerId,
    jobId,
  );

  if (!phone) return null;

  const numberClass =
    'tnum text-[14px] font-medium text-accent hover:underline disabled:opacity-60';

  return (
    <>
      <span className={cx('inline-flex flex-wrap items-center gap-x-2.5 gap-y-1', className)}>
        {textOnly ? null : canDial ? (
          <button
            type="button"
            onClick={() => call.mutate()}
            disabled={call.isPending}
            className={numberClass}
            title={`Call ${formatPhone(phone)}`}
          >
            {formatPhone(phone)}
          </button>
        ) : (
          <a href={`tel:${phone}`} className={numberClass} title={`Call ${formatPhone(phone)}`}>
            {formatPhone(phone)}
          </a>
        )}

        {canText ? (
          <Button
            size={textOnly ? 'md' : 'sm'}
            icon="chat"
            onClick={() => setTexting(true)}
          >
            {textOnly ? 'Send a text' : 'Text'}
          </Button>
        ) : (
          <a href={`sms:${phone}`}>
            <Button size={textOnly ? 'md' : 'sm'} icon="chat">
              {textOnly ? 'Send a text' : 'Text'}
            </Button>
          </a>
        )}
      </span>

      <SendSmsModal
        open={texting}
        onClose={() => setTexting(false)}
        phone={phone}
        customerId={customerId}
        jobId={jobId}
        customerName={customerName}
        onSent={(warning) =>
          setToast(
            warning
              ? { message: warning, tone: 'error' }
              : { message: 'Text sent', tone: 'success' },
          )
        }
      />

      {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
    </>
  );
}

export function CallActions({
  phone,
  customerId,
  jobId,
  customerName,
  size = 'sm',
  className,
}: CallActionsProps) {
  const { call, toast, setToast, texting, setTexting, canDial, canText } = useCallAndText(
    phone,
    customerId,
    jobId,
  );

  if (!phone) return null;

  return (
    <>
      <span className={cx('inline-flex items-center gap-1.5', className)}>
        {canDial ? (
          <Button
            size={size}
            onClick={() => call.mutate()}
            loading={call.isPending}
            title={`Call ${formatPhone(phone)}`}
          >
            ✆ Call
          </Button>
        ) : (
          <a href={`tel:${phone}`}>
            <Button size={size} title={`Call ${formatPhone(phone)}`}>
              ✆ Call
            </Button>
          </a>
        )}

        {canText ? (
          <Button size={size} onClick={() => setTexting(true)}>
            ✉ Text
          </Button>
        ) : (
          <a href={`sms:${phone}`}>
            <Button size={size}>✉ Text</Button>
          </a>
        )}
      </span>

      <SendSmsModal
        open={texting}
        onClose={() => setTexting(false)}
        phone={phone}
        customerId={customerId}
        jobId={jobId}
        customerName={customerName}
        onSent={(warning) =>
          setToast(
            warning
              ? { message: warning, tone: 'error' }
              : { message: 'Text sent', tone: 'success' },
          )
        }
      />

      {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
    </>
  );
}

export function SendSmsModal({
  open,
  onClose,
  phone,
  customerId,
  jobId,
  customerName,
  onSent,
}: {
  open: boolean;
  onClose: () => void;
  phone: string | null | undefined;
  customerId?: string | null;
  jobId?: string | null;
  customerName?: string;
  onSent: (warning?: string) => void;
}) {
  const qc = useQueryClient();
  const [body, setBody] = useState('');

  const send = useMutation({
    mutationFn: () =>
      api.messages.send({
        body: body.trim(),
        toNumber: phone ?? undefined,
        customerId,
        jobId,
      }),
    onSuccess: (result) => {
      setBody('');
      void qc.invalidateQueries({ queryKey: ['messages'] });
      onSent(result.warning);
      onClose();
    },
  });

  const segments = smsSegments(body);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={customerName ? `Text ${customerName}` : 'Send a text'}
      description={phone ? formatPhone(phone) : undefined}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!body.trim()}
            loading={send.isPending}
            onClick={() => send.mutate()}
          >
            Send
          </Button>
        </>
      }
    >
      <Field
        label="Message"
        hint={segments > 1 ? `${body.length} characters · ${segments} segments` : undefined}
      >
        <Textarea
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Running about twenty minutes behind — Marisol will be with you by 2:30."
          autoFocus
        />
      </Field>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {QUICK_REPLIES.map((reply) => (
          <button
            key={reply}
            onClick={() => setBody(reply)}
            className="rounded-full border border-line-2 px-2.5 py-1 text-[12px] text-ink-2 hover:border-accent hover:text-accent"
          >
            {reply}
          </button>
        ))}
      </div>

      {send.isError && (
        <p className="mt-3 rounded-lg bg-danger-soft px-3 py-2 text-[13px] text-danger">
          {(send.error as { message?: string }).message ?? 'Could not send'}
        </p>
      )}
    </Modal>
  );
}

/** The three texts an office sends fifty times a week. */
const QUICK_REPLIES = [
  'Running a little behind — we will call before we arrive.',
  'We are on our way now.',
  'Sorry we missed you — please give us a call back.',
];
